import { useEffect, useState, useRef } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function VisionOverlay() {
  const [unlocked, setUnlocked] = useState(false);
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const topRef = useRef<HTMLDivElement | null>(null);

  // Sørg for at overlayet starter øverst når det vises
  useEffect(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "instant", block: "start" as ScrollLogicalPosition });
    // fallback uansett:
    if (typeof window !== "undefined") window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  useEffect(() => {
    const flag = typeof window !== "undefined" && localStorage.getItem("vl_unlocked");
    if (flag === "true") setUnlocked(true);
  }, []);

  const tryUnlock = () => {
    if (pwd.trim().toUpperCase() === "KATTENMILA") {
      setUnlocked(true);
      localStorage.setItem("vl_unlocked", "true");
    } else {
      setError("Feil passord");
      setTimeout(() => setError(""), 1600);
    }
  };

  if (unlocked) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Bakgrunnsbilde */}
      <div
        className="absolute inset-0 bg-center bg-cover"
        style={{
          backgroundImage: "url('https://i.ibb.co/V0KHzz5P/vision-placeholder.jpg')",
        }}
      />

      {/* En mørk gradient for kontrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-black/90 backdrop-blur-[12px]" />

      {/* Innhold: topp-ankret, scrollbart */}
      <div
        ref={topRef}
        className="relative h-full w-full overflow-y-auto"
      >
        <div className="px-4">
          <div className="max-w-5xl mx-auto text-center text-white pt-16 md:pt-20 pb-28">
            {/* Logo + navn */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <img src="/bukken.png" alt="Væren" className="h-10 w-10 brightness-200 contrast-125" />
              <span className="text-xl font-semibold tracking-tight text-white">Gjesdalivery</span>
            </div>

            {/* Hero-tekst */}
            <h1
              className="text-5xl md:text-6xl font-extrabold tracking-tight mb-6
                         bg-gradient-to-b from-white to-gray-200 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
            >
              Visjonen vår
            </h1>

            <p
              className="mt-6 text-xl md:text-2xl font-semibold leading-relaxed
                         bg-gradient-to-b from-white to-gray-300 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]
                         max-w-3xl mx-auto"
            >
              Vi bygger Gjesdal sin egen leveringsplattform. En enkel, ærlig tjeneste som
              støtter lokale restauranter og butikker, gir ungdom fleksible jobber og
              leverer alt fra middag til lørdagsgodt. Væren står for pålitelighet i
              ruskevær og fellesskap i finvær.
            </p>

            <p
              className="mt-4 text-lg md:text-xl font-semibold leading-relaxed
                         bg-gradient-to-b from-white to-gray-300 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]
                         max-w-3xl mx-auto"
            >
              Målet er å støtte lokale bedrifter, gi Gjesdalbuere en enklere hverdag, og
              tilby ungdom trygge, godt betalte jobber – en konkret måte å skape lokale
              muligheter og forebygge utenforskap.
            </p>

            {/* Hvem er vi */}
            <h2
              className="mt-16 text-3xl md:text-4xl font-bold tracking-tight mb-4
                         bg-gradient-to-b from-white to-gray-200 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
            >
              Hvem er vi
            </h2>
            <p
              className="text-lg md:text-xl font-semibold leading-relaxed
                         bg-gradient-to-b from-white to-gray-300 bg-clip-text text-transparent drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)]
                         max-w-3xl mx-auto"
            >
              Startet av unge lokale fra Gjesdal som så et behov – og bestemte seg for å fikse det.
              En innovativ tjeneste bygget med kjærlighet til bygda, som gir tilbake til samfunnet
              og skaper arbeidsmuligheter for ungdom. Enkelt sagt: kortreist teknologi, ekte verdier,
              og lokalt fellesskap.
            </p>

            {/* Leveringssoner + kart i HVIT boks */}
            <div className="mt-20">
              <div className="max-w-6xl mx-auto text-left">
                <div className="rounded-2xl bg-white/95 dark:bg-white/90 text-gray-900 shadow-2xl border border-white/80 overflow-hidden backdrop-blur-md">
                  <div className="p-6 md:p-8">
                    <h3 className="text-2xl md:text-3xl font-bold mb-2">Leveringssoner</h3>
                    <p className="text-gray-600 mb-6">
                      Under konstruksjon. Slik ser planen omtrent ut ved lansering.
                    </p>

                    {/* Sone-bokser */}
                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-lg font-bold mb-1">Sone 1 – Ålgård Sentrum</h4>
                        <p className="text-2xl font-extrabold mb-1">39 kr</p>
                        <p className="text-gray-700">Innenfor 1 km radius fra Ålgård sentrum</p>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-lg font-bold mb-1">Sone 2 – Utvidet Ålgård</h4>
                        <p className="text-2xl font-extrabold mb-1">49 kr</p>
                        <p className="text-gray-700">Innenfor 3 km radius, men utenfor 1 km</p>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-lg font-bold mb-1">Sone 3 – Resten av Gjesdal</h4>
                        <p className="text-2xl font-extrabold mb-1">79–99 kr</p>
                        <p className="text-gray-700">
                          Alle områder utenfor 3 km, men fortsatt innenfor Gjesdal kommune
                        </p>
                      </div>
                    </div>

                    {/* Kart */}
                    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                      <div
                        style={{
                          WebkitOverflowScrolling: "touch",
                          overflowY: "auto",
                          lineHeight: 0,
                        }}
                      >
                        <iframe
                          style={{ overflow: "hidden", width: "100%", height: "460px" }}
                          allow="geolocation *; camera *;"
                          frameBorder="0"
                          src="https://www.mapotic.com/se-hvilke-omrader-vi-leverer-til/embed?zoom=10&lat=58.780271756731224&lng=6.071417668361889"
                          title="Leveringskart"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Passordkort nederst */}
            <div className="mt-20 mx-auto max-w-md">
              <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)] p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" className="text-white/90">
                    <path fill="currentColor" d="M12 17a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 17m6-6h-1V9a5 5 0 1 0-10 0v2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2m-9-2a3 3 0 0 1 6 0v2H9z"/>
                  </svg>
                  <p className="text-sm font-semibold text-white/90">Skriv inn passord for å åpne hele siden</p>
                </div>

                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && tryUnlock()}
                    placeholder="•••••••••••"
                    className="w-full rounded-xl border border-white/20 bg-white/10 text-white px-4 py-3 pr-11 font-medium focus:outline-none focus:ring-2 focus:ring-white/30 placeholder:text-white/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute inset-y-0 right-0 px-3 text-white/70 hover:text-white"
                    aria-label={showPwd ? "Skjul passord" : "Vis passord"}
                  >
                    {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

                <button
                  onClick={tryUnlock}
                  className="mt-5 w-full rounded-xl bg-white text-gray-900 font-semibold py-3 hover:bg-gray-100 transition-colors"
                >
                  Gå videre
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
